import { useEffect, useRef } from "react";
import {
  attachStreetAutocomplete,
  hasGooglePlacesApiKey,
  type GooglePlacesAddressPatch,
} from "@/lib/googlePlaces";

type Options = {
  inputRef: React.RefObject<HTMLInputElement | null>;
  apiKey: string | null | undefined;
  enabled: boolean;
  onPlaceSelected: (patch: GooglePlacesAddressPatch) => void;
};

/**
 * Hängt Google-Places-Autocomplete an das Straßenfeld (Suchfeld = Straße).
 */
export function useGooglePlacesStreetAutocomplete({
  inputRef,
  apiKey,
  enabled,
  onPlaceSelected,
}: Options): void {
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  onPlaceSelectedRef.current = onPlaceSelected;

  useEffect(() => {
    if (!enabled || !hasGooglePlacesApiKey(apiKey)) {
      return;
    }

    const input = inputRef.current;
    if (!input) {
      return;
    }

    let handle: { destroy: () => void } | null = null;
    let cancelled = false;

    void attachStreetAutocomplete({
      input,
      apiKey: apiKey!.trim(),
      onPlaceSelected: (patch) => onPlaceSelectedRef.current(patch),
    })
      .then((h) => {
        if (cancelled) {
          h.destroy();
          return;
        }
        handle = h;
      })
      .catch(() => {
        /* API-Key ungültig oder Netzwerk – Formular bleibt manuell nutzbar */
      });

    return () => {
      cancelled = true;
      handle?.destroy();
    };
  }, [apiKey, enabled, inputRef]);
}
